import * as React from 'react'
import { StudioButton } from '@repo/studio-components/studio-button'
import { IconSearch } from '@repo/design-system-cf/components/icons/index'

import { useRouterUtil, useTranslations } from '../../contexts/header-config-context'
import { tw } from 'utils/classnames'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'

interface SearchInputProps {
  autofocus?: boolean
  value: string
  onChange: (newValue: string) => void
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onHover?: React.MouseEventHandler<HTMLInputElement>
  onClick?: VoidFunction
  aiSearchSlot?: React.ReactNode
}

export const SearchInput = function SearchInput({
  ref,
  autofocus,
  value,
  onChange,
  onHover,
  onKeyPress,
  onFocus,
  onBlur,
  aiSearchSlot,
}: SearchInputProps & {
  ref?: React.RefObject<unknown | null>
}) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const pathname = useRouterUtil().pathname
  const t = useTranslations()

  React.useImperativeHandle(ref, () => inputRef.current, [])

  React.useEffect(() => {
    if (autofocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [autofocus])

  //remove focus from input when route changes
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.blur()
    }
  }, [pathname])

  return (
    <label
      className={tw(
        'rounded-2 flex-center border-cf-dreamy-cloud-600 focus-within:border-cf-violet-hover bg-cf-dreamy-cloud-200 group flex w-full overflow-hidden border pl-[20px] font-normal transition-colors',
      )}
    >
      <input
        ref={inputRef}
        data-testid="search-input"
        type="text"
        autoComplete="off"
        spellCheck="false"
        dir="auto"
        value={value}
        onChange={e => onChange(e.target.value)}
        onMouseOver={onHover}
        placeholder={t('search.placeholder')}
        onKeyDown={onKeyPress}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={() => {
          sendAmplitudeEvent(AmplitudeEvents.SEARCH_INPUT_CLICKED, {
            source: 'header',
          })
        }}
        className={tw(
          'placeholder-cf-dreamy-cloud-900 text-14 my-auto ml-2 h-5 w-full border-none bg-transparent outline-none transition-opacity placeholder:opacity-100 group-focus-within:placeholder:opacity-0',
        )}
      />
      {aiSearchSlot}
      <StudioButton
        type="submit"
        variant="invisible"
        data-testid="search-button"
        aria-label="Go to search page"
        className="group-focus-within:text-cf-dreamy-cloud-900 group-focus-within:bg-cf-violet -mr-[1px] rounded-l-none rounded-r-2 transition-colors"
      >
        <IconSearch className="text-cf-dreamy-cloud-900 group-focus-within:text-cf-white h-4 w-4 transition-colors" />
      </StudioButton>
    </label>
  )
}
